import React, {
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
    useState,
} from "react";
import { useSelector } from "react-redux";
import Message from "../Message";
import MessageLoader from "../MessageLoader";
import LinkCard from "../LinkCard";
import NewsSourceCards from "../NewsSourceCards";
import HarrisChainOfThought from "../HarrisChainOfThought";
import {
    selectMessages,
    selectIsAssistantResponding,
    selectIsTyping,
    selectErrorMessage,
    selectTaskStatusMessage,
    selectIsChatOpen,
} from "../../Selectors/AssistantSelector";
import AssistantThinkingLoader from "../AssistantThinkingLoader";
import NewsArticlesAccordian from "../NewsArticlesAccordian";
import SourcesPill from "../SourcesPill";
import "./MessageContent.scss";
import "../Message/Message.scss";
import Welcome from "../Welcome";
import ErrorBanner from '../ErrorBanner'
import IntroWelcomeMessage from "../IntroWelcomeMessage";
import { AI_ASSISTANT_CONSTANTS } from "../../constants/aiAssistantConstants";

const MessageContent = forwardRef((props, ref) => {
    const messages = useSelector(selectMessages);
    const isUserTyping = useSelector(selectIsTyping);
    const isAssistantResponding = useSelector(selectIsAssistantResponding);
    const errorMessage = useSelector(selectErrorMessage);
    const taskStatusMessage = useSelector(selectTaskStatusMessage);
    const isChatOpen = useSelector(selectIsChatOpen);
    const activeConversationId = useSelector(
        (state) => state.chat?.activeConversationId ?? null,
    );
    const userId = useSelector((state) => state.userDetail?.id ?? null);
    const [statusTrail, setStatusTrail] = useState([]);
    // The turn's start, kept apart from the steps it is displayed beside.
    const [turnStartedAt, setTurnStartedAt] = useState(null);
    const [isTaskStatusClosed, setIsTaskStatusClosed] = useState(false);
    const [hasShownIntroThisSession, setHasShownIntroThisSession] = useState(false);
    const [hasInteractedThisSession, setHasInteractedThisSession] = useState(false);

    const messageEndRef = useRef(null);
    // Which conversation the last scroll belonged to. Opening a different one
    // means the transcript arrives whole, and the jump to the bottom must not
    // be animated - see scrollToBottom.
    const scrolledConversationRef = useRef(null);
    const previousTypes = [];

    const getPreviousMsgType = (msgType, index) => {
        previousTypes.push(msgType);
        return index === 0 ? "" : previousTypes[index - 1];
    };

    /**
     * Pins the transcript to the bottom.
     *
     * The container sets `scroll-behavior: smooth`, which is right while a
     * reply streams in - the view glides a line or two to follow it. It is
     * wrong when a conversation opens: the whole transcript arrives at once,
     * and the browser animates the entire height, so the reader watches weeks
     * of history race past before landing where they expected to start.
     *
     * `instant` overrides the CSS for that one call.
     */
    const scrollToBottom = (instant = false) => {
        const container = messageEndRef.current;
        if (!container) return;
        // `scrollTo` is how the CSS behaviour is overridden, but it is not
        // present everywhere (jsdom, older embedded webviews). Falling back to
        // `scrollTop` still lands in the right place - it just animates.
        if (instant && typeof container.scrollTo === "function") {
            container.scrollTo({ top: container.scrollHeight, behavior: "instant" });
        } else {
            container.scrollTop = container.scrollHeight;
        }
        handleScroll();
    };

    useImperativeHandle(ref, () => ({
        scrollToBottom,
    }));

    useEffect(() => {
        // A conversation that just opened jumps; one that is being added to
        // glides. Both land in the same place, but only one of them should
        // look like travelling there.
        const opened = scrolledConversationRef.current !== activeConversationId;
        const timer = setTimeout(() => {
            scrollToBottom(opened);
            scrolledConversationRef.current = activeConversationId;
        }, 100);
        return () => clearTimeout(timer);
    }, [messages, isAssistantResponding, errorMessage, activeConversationId]);

    useEffect(() => {
        if (!statusTrail.length) return;
        const timer = setTimeout(() => {
            scrollToBottom();
        }, 100);
        return () => clearTimeout(timer);
    }, [statusTrail]);

    const normalizeStatusEntry = (status) => {
        const phase = status?.taskTitle || status?.stage || status?.message || "Working on it...";
        const detail = status?.taskMessage || status?.message || "";
        return {
            phase,
            detail,
            state: status?.type === "error" ? "error" : "active",
            timestamp: Date.now(),
        };
    };

    useEffect(() => {
        if (!taskStatusMessage) return;
        if (taskStatusMessage?.type === "task_status_end") {
            setStatusTrail([]);
            setTurnStartedAt(null);
            setIsTaskStatusClosed(true);
            return;
        }
        setIsTaskStatusClosed(false);
        setTurnStartedAt((prev) => prev ?? Date.now());
        setStatusTrail((prev) => {
            const nextStatus = normalizeStatusEntry(taskStatusMessage);
            const last = prev[prev.length - 1];
            const isDuplicate =
                last &&
                last.phase === nextStatus.phase &&
                last.detail === nextStatus.detail;
            if (isDuplicate) {
                return prev;
            }
            return [...prev, nextStatus];
        });
    }, [taskStatusMessage]);

    useEffect(() => {
        if (isAssistantResponding) {
            setIsTaskStatusClosed(false);
            return;
        }
        setStatusTrail([]);
        setTurnStartedAt(null);
        setIsTaskStatusClosed(false);
    }, [isAssistantResponding]);

    const handleScroll = () => {
        if (!messageEndRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = messageEndRef.current;
        // Check if we are not at the bottom (allow a small threshold)
        const isNotAtBottom = scrollHeight - scrollTop - clientHeight > 10;
        
        // Dispatch an event so the parent can toggle the shadow class
        const event = new CustomEvent("chat-scroll-state", { 
            detail: { isScrolled: isNotAtBottom && messages.length > 0 } 
        });
        window.dispatchEvent(event);
    };

    useEffect(() => {
        const container = messageEndRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            // Initial check
            handleScroll();
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [messages.length]);

    useEffect(() => {
        if (messages.length === 0) {
            setStatusTrail([]);
            setTurnStartedAt(null);
            setIsTaskStatusClosed(true);
            return;
        }
        // As soon as there is any message in this tab, we consider the user to have interacted.
        if (!hasInteractedThisSession) {
            setHasInteractedThisSession(true);
        }
    }, [messages.length, hasInteractedThisSession]);

    const statusSteps = statusTrail.map((status, index) => ({
        title: status.phase,
        items: status.detail ? [status.detail] : [],
        state:
            status.state === "error"
                ? "error"
                : index === statusTrail.length - 1
                    ? "active"
                    : "done",
    }));
    const showChainOfThought =
        isAssistantResponding && !isTaskStatusClosed && statusTrail.length > 0;

    // Show the inline thinking indicator when the assistant is responding
    // but no streaming text has appeared yet in the message list.
    const lastMsg = messages[messages.length - 1];
    const hasStreamingText =
        lastMsg &&
        (lastMsg.type || "").toLowerCase() === "harris" &&
        (lastMsg.message_type || "text") === "text" &&
        (lastMsg.payload?.text || lastMsg.text || "").length > 0;
    const showThinkingIndicator =
        isAssistantResponding && !hasStreamingText && !showChainOfThought;

    const introSeenKey = (() => {
        if (typeof window === "undefined") return null;
        if (userId == null) return null;
        return `${AI_ASSISTANT_CONSTANTS.STORAGE_KEYS.AI_ASSISTANT_INTRO_SEEN_PREFIX}${userId}`;
    })();
    const hasSeenIntroFromStorage = (() => {
        if (!introSeenKey) return false;
        try {
            return window.localStorage.getItem(introSeenKey) === "1";
        } catch {
            return false;
        }
    })();
    const shouldShowIntroWelcome =
        isChatOpen &&
        messages.length === 0 &&
        Boolean(introSeenKey) &&
        !hasInteractedThisSession &&
        (!hasSeenIntroFromStorage || hasShownIntroThisSession);

    useEffect(() => {
        if (!introSeenKey) return;
        if (!shouldShowIntroWelcome) return;
        if (hasShownIntroThisSession) return;

        setHasShownIntroThisSession(true);

        if (!hasSeenIntroFromStorage) {
            try {
                window.localStorage.setItem(introSeenKey, "1");
            } catch {
                // Ignore storage failures (private mode, quota, etc.)
            }
        }
    }, [shouldShowIntroWelcome, introSeenKey, hasSeenIntroFromStorage, hasShownIntroThisSession]);

    return (
        <div className="card-content">
            {messages.length === 0 ? (
                shouldShowIntroWelcome ? (
                    <ul
                        className="msg-container"
                        ref={messageEndRef}
                        role="log"
                        aria-live="polite"
                        aria-label="Chat messages"
                    >
                        <IntroWelcomeMessage />
                    </ul>
                ) : (
                    <Welcome />
                )
            ) : (
                <ul className="msg-container" ref={messageEndRef} role="log" aria-live="polite" aria-label="Chat messages">
                    {messages.map((msg, index) => {
                        // A turn opens an empty reply slot as soon as the
                        // message is accepted, so the deltas have somewhere to
                        // land. Until the first one arrives there is nothing
                        // in it, and drawing it puts an empty bubble under the
                        // question.
                        //
                        // `stopped` is the exception: the user pressed stop
                        // before any text arrived, and the reply renders as the
                        // note saying so. Every other empty reply is covered by
                        // something else on screen - the thinking indicator
                        // while it runs, the error banner when it fails - so
                        // drawing it would only add a blank bubble.
                        const isEmptyReply =
                            msg.turn_id &&
                            msg.type !== "You" &&
                            !(msg.payload?.text || msg.text || "").length;
                        if (isEmptyReply && msg.turn_status !== "stopped") return null;

                        const previousType = getPreviousMsgType(msg.type, index);
                        const messageType = msg.message_type || "text";
                        const payload = { ...(msg.payload || {}), ...msg };
                        if (messageType === "link") {
                            const link = payload.link ?? "";
                            const title = payload.title ?? "Link";
                            const description = payload.description ?? payload.text ?? "";
                            return (
                                <li
                                    key={index}
                                    className="msg-text align-left chat-item chat-item--link show"
                                >
                                    <div className="chat-block">
                                        <LinkCard
                                            title={title}
                                            description={description}
                                            link={link}
                                        />
                                    </div>
                                </li>
                            );
                        }
                        if (messageType === "sources") {
                            const articles = Array.isArray(payload.sources) ? payload.sources : [];
                            const grounding = Array.isArray(payload.grounding_sources) ? payload.grounding_sources : [];
                            if (articles.length === 0 && grounding.length === 0) return null;
                            return (
                                <li
                                    key={index}
                                    className="msg-text align-left chat-item chat-item--sources show"
                                >
                                    <div className="chat-block">
                                        <NewsSourceCards
                                            articles={articles}
                                            source_type={payload.source_type}
                                            grounding_sources={grounding}
                                        />
                                    </div>
                                </li>
                            );
                        }
    // Replace the standalone query_params item to avoid double rendering
                        if (messageType === "query_params") {
                            return null;
                        }
                        return (
                            <Message
                                key={index}
                                msg={msg}
                                previousType={previousType}
                                scrollToBottom={scrollToBottom}
                                messageIndex={index}
                            />
                        );
                    })}
                    {showThinkingIndicator && (
                        <li className="msg-text align-left show thinking-indicator-row">
                            <AssistantThinkingLoader />
                        </li>
                    )}
                    {isUserTyping && <MessageLoader type="You" />}
                    {errorMessage && <ErrorBanner message={errorMessage} />}
                </ul>
            )}
            {showChainOfThought && (
                <div className="cot-wrapper">
                    <HarrisChainOfThought
                        steps={statusSteps}
                        startedAt={turnStartedAt}
                    />
                </div>
            )}
        </div>
    );

});

export default MessageContent;
